<script setup>
import CLink from "@/Components/CLink.vue"
import {computed, ref, watch} from "vue"
import {usePage} from "@inertiajs/vue3"
import {helpers} from "../Services/helpers.js"

const page = usePage()
const props = defineProps({
    product: Object,
    canViewStock: Boolean,
    canViewPrice: Boolean,
})

const showSidebar = ref(false)
const hoveredColor = ref(null)
const hoveredImage = ref(null)
const itemProfile = props.product.item_profile

// caso exista, exibe o nome da cor ativa ou da cor da variação sob o mouse
const colorTitle = computed(() => {
    if (hoveredColor.value) {
        return hoveredColor.value.reference
    } else {
        return props.product.reference
    }
})

/** Vai apenas exibir os valores do estoque que já foram definidos no back-end */
const getStock = (providerType) => computed(() => {
    //Se houver uma cor no hever do slide, exibir valor do estoque dessa cor
    if (hoveredColor.value) {
        return providerType === 'local' ? hoveredColor.value.stock.local : hoveredColor.value.stock.fornecedor
    }

    //Se o produto tiver apenas uma variação, exibir valor do estoque dessa variação
    if (props.product.variations?.length === 1) {
        return providerType === 'local' ? props.product.variations[0].stock.local : props.product.variations[0].stock.fornecedor
    }

    //Se o produto tiver mais de uma variação, exibir que o estoque mudará por variação
    return providerType === 'local' ? props.product.stock.local : props.product.stock.fornecedor
})

// imagem alternativa (se não houver a imagem principal)
const showHoveredImg = ref(false)

const mainImage = computed(() => {
    if (props.product?.media_url) {
        return props.product.media_url
    }

    // Se não tiver, percorre todas as variações até encontrar uma URL válida
    if (props.product?.variations?.length > 0) {
        for (const variation of props.product.variations) {
            if (variation.media_url) {
                return variation.media_url
            }
        }
    }

    return '/assets/img/no-photo.png'
})

const hoveredMainImage = computed(() => {
    if (props.product?.media_url) {
        return props.product.media_url
    }

    if (props.product?.variations?.length > 0) {
        for (const variation of props.product.variations) {
            if (variation.media_url) {
                return variation.media_url
            }
        }
    }

    return '/assets/img/no-photo.png'
})


const dimensionString = computed(() => {
    const dimensions = []

    if (props.product.width && props.product.width > 0) {
        dimensions.push(props.product.width)
    }
    if (props.product.height && props.product.height > 0) {
        dimensions.push(props.product.height)
    }
    if (props.product?.length && props.product?.length > 0) {
        dimensions.push(props.product.length)
    }

    if (dimensions.length < 2) {
        return ''
    } else {
        return dimensions.join('x') + 'mm'
    }
})

const hideSlideColor = computed(() => {
    return !page.props.selectedColor;
})

const canShowStock = function(providerType){
    if (hoveredColor.value) {
        return props.canViewStock && hoveredColor.value.stock && hoveredColor.value.stock[providerType]
    }

    if (props.product.variations?.length === 1) {
        return props.canViewStock && props.product.variations[0].stock && props.product.variations[0].stock[providerType]
    }

    return props.canViewStock && props.product.stock && props.product.stock[providerType]
}


// exibe imagem principal da variação que esta sob o mouse
watch(hoveredColor, (variation) => {
    hoveredImage.value = variation ? variation.media_url : null
})
</script>

<template>
    <v-card class="pa-4 infos overflow-visible" outlined :elevation="showSidebar ? 4 : 2" @mousemove="showSidebar = true" @mouseleave="showSidebar = false">
        <v-row class="position-relative" justify="center" align="center">
            <v-col>
                <c-link :href=props.product.detail_url preserve-scroll preserve-state>
                    <v-sheet class="position-relative" @mouseenter="showHoveredImg = true" @mouseleave="showHoveredImg = false">
                        <v-img :src="hoveredImage || mainImage" alt="Imagem do Produto" style="height: 216px" eager>
                            <template v-slot:placeholder>
                                <div class="d-flex align-center justify-center fill-height">
                                    <v-progress-circular color="grey-lighten-4" indeterminate></v-progress-circular>
                                </div>
                            </template>
                        </v-img>
                        <v-fade-transition>
                            <v-img v-if="hoveredMainImage && showHoveredImg" :src="hoveredMainImage" alt="Imagem alternativa do Produto"
                                   style="width: 100%; height: 216px" eager class="position-absolute top-0 left-0">
                                <template v-slot:placeholder>
                                    <div class="d-flex align-center justify-center fill-height">
                                        <v-progress-circular
                                            color="grey-lighten-4"
                                            indeterminate
                                        ></v-progress-circular>
                                    </div>
                                </template>
                            </v-img>
                        </v-fade-transition>
                    </v-sheet>
                    <v-card-title class="pb-0">
                        {{ itemProfile.product_title }}
                    </v-card-title>
                    <v-card-subtitle>
                        {{ colorTitle }}
                    </v-card-subtitle>
                    <v-card-text>
                        <v-sheet style="min-height: 60px">
                            <p class="three-line-truncate">{{ itemProfile.description }}</p>
                        </v-sheet>
                        <v-sheet class="my-2 text-medium-emphasis" height="40px">
                            <template v-if="dimensionString"><span class="font-weight-bold">Tamanho:</span>{{ itemProfile.diameter }}<br/></template>
                            <template v-if="canShowStock('local')"><span class="font-weight-bold">Estoque Local:</span> {{ getStock('local') }}<br/></template>
                            <template v-if="canShowStock('fornecedor')"><span class="font-weight-bold">Estoque Fornecedor:</span> {{ getStock('fornecedor') }}</template>
                        </v-sheet>
                        <v-sheet v-if="itemProfile.max_price !== undefined" class="pt-2">
                            <br/>
                            <p v-if="canViewPrice" class="text-right font-weight-bold text-h5 pt-2">R$ {{ helpers.formatPrice(itemProfile.min_price) }}
                                <template v-if="itemProfile.min_price !== itemProfile.max_price"> - {{ helpers.formatPrice(itemProfile.max_price) }}</template>
                            </p>
                        </v-sheet>
                        <v-sheet v-if="itemProfile.max_price === undefined" class="pt-2">
                            <br/>
                            <p v-if="canViewPrice" class="text-right font-weight-bold text-h5 pt-2">R$ </p>
                        </v-sheet>
                    </v-card-text>
                </c-link>
            </v-col>
            <v-slide-x-transition>
                <v-col md="3" class="position-absolute" style="right: -30px"
                       v-if="!$vuetify.display.mobile && showSidebar && product.variation_colors?.length > 1">
                    <v-card elevation="3">
                        <v-slide-group v-if="hideSlideColor" show-arrows direction="vertical" prev-icon="mdi-chevron-up" next-icon="mdi-chevron-down"
                                       style="max-height: 300px">
                            <v-slide-group-item v-for="color in product.variation_colors" :key="color.media_url">
                                <template v-for="variation in product.variations.filter(v => v.color === color.title)" :key="variation.color">
                                    <c-link preserve-scroll preserve-state
                                            @mouseover="hoveredColor = variation"
                                            @mouseleave="hoveredColor = null"
                                            :href=variation.detail_url
                                    >
                                        <v-img :src="variation.media_url || '/assets/img/no-photo.png'" class="my-1" style="min-height: 50px" eager>
                                            <template v-slot:placeholder>
                                                <div class="d-flex align-center justify-center fill-height">
                                                    <v-progress-circular color="grey-lighten-4" indeterminate/>
                                                </div>
                                            </template>
                                        </v-img>
                                    </c-link>
                                </template>
                            </v-slide-group-item>
                        </v-slide-group>
                    </v-card>
                </v-col>
            </v-slide-x-transition>
        </v-row>
    </v-card>
</template>

<style>
.three-line-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
}
</style>
